import React, { useState, useEffect, useRef } from 'react';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';
import UserSelector from './UserSelector';
import { useAuth } from '../contexts/AuthContext';
import { useFeeSettings } from '../utils/feeSettings';
import {
  isStartupClientTypeName,
  isSmallEnterpriseStyleClientTypeName
} from '../utils/trademarkClientTypes';

const TrademarkStep1 = ({ application, settings, masterDataList, onComplete, saving, onPreviousStep, canEdit = true }) => {
  const { user: currentUser } = useAuth();
  const { showFees, loading: feeSettingsLoading } = useFeeSettings();
  
  const [formData, setFormData] = useState({
    masterDataId: '',
    country: '',
    entityType: '',
    turnover: '',
    documents: {},
    udyogAadhaarAssistance: false // Added for Udyog Aadhaar Assistance option
  });

  const [selectedMasterData, setSelectedMasterData] = useState(null);
  const [fees, setFees] = useState(null);
  const [loadingFees, setLoadingFees] = useState(false);
  const [errors, setErrors] = useState({});
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [uploadingDocuments, setUploadingDocuments] = useState({});
  const [generatingDocuments, setGeneratingDocuments] = useState({});
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [clientTypes, setClientTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filteredMasterDataList, setFilteredMasterDataList] = useState([]);
  const [issueToClient, setIssueToClient] = useState(false); // New state for invoice client option
  const [userHasInteracted, setUserHasInteracted] = useState(false); // Track if user has interacted with form
  const autoSaveTimeoutRef = useRef(null);



  // Auto-save function
  const autoSave = async (formDataToSave) => {
    try {
      // Only auto-save if application exists (has an ID)
      if (!application?._id) {
        console.log('Skipping auto-save - application not created yet');
        return;
      }
      
      // Convert document keys back to camelCase for backend
      const convertedDocuments = {};
      if (formDataToSave.documents) {
        Object.keys(formDataToSave.documents).forEach(docType => {
          const doc = formDataToSave.documents[docType];
          if (doc && (doc.uploaded || doc.documentId || doc.documentUrl)) {
            // Convert uppercase keys back to camelCase
            let backendKey;
            if (docType === 'UDYOG_AADHAAR') {
              backendKey = 'udyogAadhaar';
            } else if (docType === 'STARTUP_CERTIFICATE') {
              backendKey = 'startupCertificate';
            } else if (docType === 'POWER_OF_ATTORNEY') {
              backendKey = 'powerOfAttorney';
            } else {
              backendKey = docType.toLowerCase();
            }
            
            convertedDocuments[backendKey] = {
              documentId: doc.documentId,
              fileName: doc.fileName,
              documentUrl: doc.documentUrl,
              uploaded: true,
              source: doc.source
            };
          }
        });
      }
      
      setAutoSaving(true);
      await api.put(`/trademark-applications/${application._id}`, {
        ...formDataToSave,
        documents: convertedDocuments, // Use converted documents
        currentStep: 1
      });
      setLastSaved(new Date());
    } catch (error) {
      console.warn('Auto-save error:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  // Manual save function
  const manualSave = async () => {
    try {
      // Only save if application exists (has an ID)
      if (!application?._id) {
        toast.info('Application will be saved when you complete Step 1');
        return;
      }
      
      setAutoSaving(true);
      await autoSave(formData);
      toast.success('Progress saved successfully');
    } catch (error) {
      toast.error('Failed to save progress');
    } finally {
      setAutoSaving(false);
    }
  };

  // Debounced auto-save
  const debouncedAutoSave = (formDataToSave) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave(formDataToSave);
    }, 2000); // Save after 2 seconds of inactivity
  };

  // Fetch client types and users
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientTypesResponse, usersResponse] = await Promise.all([
          api.get('/admin/client-types'),
          api.get('/users')
        ]);
        setClientTypes(clientTypesResponse.data);
        setUsers(usersResponse.data.data || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    
    fetchData();
  }, []);

  // Set current user as default assigned user and filter master data
  useEffect(() => {
    if (currentUser) {
      // Set current user as default selected user if not already set
      if (!selectedUser) {
        setSelectedUser(currentUser);
      }
      
      // Filter master data based on selected user (or show all if no user selected)
      if (selectedUser) {
        console.log('Filtering master data for user:', selectedUser);
        console.log('Master data list length:', masterDataList.length);
        
        const filtered = masterDataList.filter(masterData => {
          const createdBy = masterData.createdBy;
          const userId = selectedUser._id || selectedUser;
          
          // Handle both populated and unpopulated createdBy field
          const createdById = createdBy?._id || createdBy;
          
          // If createdBy is null/undefined, include the master data (show all)
          if (!createdById) {
            console.log('Master data has no createdBy, including:', masterData.clientName);
            return true;
          }
          
          console.log('Comparing:', { createdById, userId, match: createdById === userId });
          return createdById === userId;
        });
        
        console.log('Filtered master data result:', filtered.length, 'items');
        setFilteredMasterDataList(filtered);
      } else {
        // If no user selected, show all master data
        console.log('No user selected, showing all master data');
        setFilteredMasterDataList(masterDataList);
      }
    } else if (masterDataList.length > 0) {
      // Fallback: if no current user, show all master data
      console.log('No current user, showing all master data as fallback');
      setFilteredMasterDataList(masterDataList);
    }
  }, [currentUser, selectedUser, masterDataList]);

  // Update filtered master data when masterDataList changes
  useEffect(() => {
    if (currentUser && masterDataList.length > 0) {
      if (selectedUser) {
        console.log('Updating filtered list due to masterDataList change for user:', selectedUser);
        
        const filtered = masterDataList.filter(masterData => {
          const createdBy = masterData.createdBy;
          const userId = selectedUser._id || selectedUser.id || selectedUser;
          
          // Handle both populated and unpopulated createdBy field
          const createdById = createdBy?._id || createdBy;
          
          // If createdBy is null/undefined, include the master data (show all)
          if (!createdById) {
            console.log('Master data has no createdBy, including:', masterData.clientName);
            return true;
          }
          
          return createdById === userId;
        });
        
        console.log('Updated filtered master data:', filtered.length, 'items');
        setFilteredMasterDataList(filtered);
      } else {
        // If no user selected, show all master data
        console.log('No user selected, showing all master data');
        setFilteredMasterDataList(masterDataList);
      }
    }
  }, [masterDataList, currentUser, selectedUser]);

  // Retry finding master data when masterDataList changes (in case application loaded first)
  useEffect(() => {
    if (application?.masterDataId && masterDataList.length > 0 && !selectedMasterData) {
      console.log('Retrying master data lookup after masterDataList loaded');
      
      // Handle both populated and unpopulated masterDataId
      const masterDataId = application.masterDataId._id || application.masterDataId;
      console.log('Looking for master data with ID:', masterDataId);
      
      // Try to find master data in filtered list first, then fall back to full list
      let masterData = filteredMasterDataList.find(md => md._id === masterDataId);
      if (!masterData && masterDataList.length > 0) {
        masterData = masterDataList.find(md => md._id === masterDataId);
      }
      
      console.log('Found master data on retry:', masterData ? masterData.clientName : 'Not found');
      setSelectedMasterData(masterData);
      
      // If master data is found, also set the form data masterDataId
      if (masterData && !formData.masterDataId && !userHasInteracted) {
        setFormData(prev => ({
          ...prev,
          masterDataId: masterData._id
        }));
      }
    }
  }, [masterDataList, filteredMasterDataList, application?.masterDataId, selectedMasterData, formData.masterDataId, userHasInteracted]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Load application data if editing
  useEffect(() => {
    if (application && application._id) {
      console.log('Loading application data:', application);
      
      // Set form data from application
      setFormData({
        masterDataId: application.masterDataId?._id || application.masterDataId || '',
        country: application.country || '',
        entityType: application.entityType?._id || application.entityType || '',
        turnover: application.turnover ? (application.turnover < 20000000 ? 'less_than_2crore' : 'more_than_2crore') : '',
        documents: {},
        udyogAadhaarAssistance: application.udyogAadhaarAssistance || false
      });
      
      // Set issueToClient from application
      setIssueToClient(application.issueToClient || false);
      
      // Set fees
      if (application.governmentFees || application.professionalFees) {
        setFees({
          governmentFees: application.governmentFees || 0,
          professionalFees: application.professionalFees || 0,
          udyogAadhaarFees: application.udyogAadhaarFees || 0,
          totalFees: application.totalFees || 0
        });
      }
    }
  }, [application]);

  useEffect(() => {
    if (application) {
      // Convert numeric turnover to dropdown value
      let turnoverValue = '';
      if (application.turnover) {
        if (application.turnover < 20000000) {
          turnoverValue = 'less_than_2crore';
        } else {
          turnoverValue = 'more_than_2crore';
        }
      }
      
      // Preserve documents from application, ensuring proper structure
      const preservedDocuments = {};
      if (application.documents) {
        Object.keys(application.documents).forEach(docType => {
          const doc = application.documents[docType];
          if (doc && (doc.uploaded || doc.documentId || doc.documentUrl)) {
            // Convert camelCase keys to uppercase with underscores for form data
            let formDataKey;
            if (docType === 'udyogAadhaar') {
              formDataKey = 'UDYOG_AADHAAR';
            } else if (docType === 'startupCertificate') {
              formDataKey = 'STARTUP_CERTIFICATE';
            } else if (docType === 'powerOfAttorney') {
              formDataKey = 'POWER_OF_ATTORNEY';
            } else {
              formDataKey = docType.toUpperCase();
            }
            
            preservedDocuments[formDataKey] = {
              documentId: doc.documentId,
              fileName: doc.fileName || `${docType}.pdf`,
              documentUrl: doc.documentUrl,
              uploaded: true,
              source: doc.source || 'application'
            };
          }
        });
      }
      
      // Only set form data if it hasn't been modified by user interaction
      // Check if this is the initial load (no user interaction yet)
      const isInitialLoad = !formData.masterDataId && !formData.country && !formData.entityType;
      
      if (isInitialLoad && !userHasInteracted) {
        setFormData({
          masterDataId: application.masterDataId?._id || '',
          country: application.country || '',
          entityType: application.entityType || '',
          turnover: turnoverValue,
          documents: preservedDocuments, // Use preserved documents
          udyogAadhaarAssistance: application.udyogAadhaarAssistance || false // Preserve Udyog Aadhaar Assistance from application
        });
        console.log('Initial load - setting form data from application');
      } else {
        console.log('User has already interacted with form - preserving user selections');
      }
      
      if (application.masterDataId) {
        console.log('Application has masterDataId:', application.masterDataId);
        console.log('Master data list length:', masterDataList.length);
        console.log('Filtered master data list length:', filteredMasterDataList.length);
        
        // Handle both populated and unpopulated masterDataId
        const masterDataId = application.masterDataId._id || application.masterDataId;
        console.log('Looking for master data with ID:', masterDataId);
        
        // Try to find master data in filtered list first, then fall back to full list
        let masterData = filteredMasterDataList.find(md => md._id === masterDataId);
        if (!masterData && masterDataList.length > 0) {
          masterData = masterDataList.find(md => md._id === masterDataId);
        }
        
        console.log('Found master data:', masterData ? masterData.clientName : 'Not found');
        setSelectedMasterData(masterData);
        
        // If master data is found, also set the form data masterDataId
        if (masterData && !formData.masterDataId && !userHasInteracted) {
          setFormData(prev => ({
            ...prev,
            masterDataId: masterData._id
          }));
        }
      }
      
      if (application.assignedUser) {
        setSelectedUser(application.assignedUser);
        
        // Update filtered master data based on assigned user
        const filtered = masterDataList.filter(masterData => {
          const createdBy = masterData.createdBy;
          const userId = application.assignedUser._id || application.assignedUser;
          
          // Handle both populated and unpopulated createdBy field
          const createdById = createdBy?._id || createdBy;
          
          // If createdBy is null/undefined, include the master data (show all)
          if (!createdById) {
            console.log('Master data has no createdBy, including:', masterData.clientName);
            return true;
          }
          
          return createdById === userId;
        });
        setFilteredMasterDataList(filtered);
      }
    }
  }, [application, masterDataList, userHasInteracted]);

  // Function to automatically set documents from master data using field mappings
  const autoSetDocumentsFromMasterData = async (masterData) => {
    if (!masterData || !settings) return;

    console.log('=== autoSetDocumentsFromMasterData called ===');
    console.log('masterData:', masterData.clientName);
    console.log('current formData.documents:', formData.documents);
    console.log('settings:', settings);

    const updatedDocuments = { ...formData.documents };
    let hasChanges = false;

    // Check each document requirement for field mappings
    const documentTypes = ['udyogAadhaar', 'startupCertificate', 'powerOfAttorney'];
    
    for (const docType of documentTypes) {
      console.log(`\n--- Processing ${docType} ---`);
      const docConfig = settings.documentRequirements[docType];
      console.log("docConfig:", docConfig);
      
      if (!docConfig?.fieldMapping?.uploadFieldId) {
        console.log(`No field mapping for ${docType}`);
        // Remove automatic Power of Attorney generation - only generate when button is clicked
        console.log(`No field mapping for ${docType}, skipping automatic generation`);
        continue;
      }

      console.log("docConfig:", docConfig);

      const { uploadFieldId } = docConfig.fieldMapping;

      console.log("uploadFieldId:", uploadFieldId);
      const uploadField = settings.uploadFields?.find(field => field._id === uploadFieldId);

      console.log("uploadField:", uploadField);
      console.log("masterData.documents:", masterData.documents);
      console.log("masterData.documents?.[uploadField.name]?.documentId:", masterData.documents?.[uploadField?.name]?.documentId);
      
      if (uploadField && masterData.documents?.[uploadField.name]?._id) {
        const masterDataDoc = masterData.documents[uploadField.name];
        
        // Convert backend name to frontend name
        let frontendKey;
        if (docType === 'udyogAadhaar') {
          frontendKey = 'UDYOG_AADHAAR';
        } else if (docType === 'startupCertificate') {
          frontendKey = 'STARTUP_CERTIFICATE';
        } else if (docType === 'powerOfAttorney') {
          frontendKey = 'POWER_OF_ATTORNEY';
        } else {
          frontendKey = docType.toUpperCase();
        }
        
        // Check if document is already set in form data
        const currentDoc = updatedDocuments[frontendKey];
        
        // Only set if not already set or if different from current
        if (!currentDoc?.documentId || currentDoc.documentId !== masterDataDoc.documentId) {
          updatedDocuments[frontendKey] = {
            documentId: masterDataDoc.documentId,
            fileName: masterDataDoc.fileName || `${docType}.pdf`,
            documentUrl: masterDataDoc.documentUrl || masterDataDoc.fileUrl,
            uploaded: true,
            source: 'masterData'
          };
          hasChanges = true;
          console.log(`Auto-set ${docType} document from master data as ${frontendKey}:`, masterDataDoc.fileName);
        } else {
          console.log(`Document ${docType} already set as ${frontendKey}, skipping auto-set`);
        }
      } else {
        console.log(`Document ${docType} not found in master data`);
        // Remove automatic Power of Attorney generation - only generate when button is clicked
        console.log(`Document ${docType} not found in master data, skipping automatic generation`);
        
        // Try to find by name matching if field mapping doesn't work
        if (masterData.documents) {
          const documentKeys = Object.keys(masterData.documents);
          for (const key of documentKeys) {
            const lowerKey = key.toLowerCase();
            let isMatch = false;
            
            if (docType === 'udyogAadhaar' && (lowerKey.includes('udyog') || lowerKey.includes('aadhaar'))) {
              isMatch = true;
            } else if (docType === 'startupCertificate' && (lowerKey.includes('startup') || lowerKey.includes('certificate'))) {
              isMatch = true;
            } else if (docType === 'powerOfAttorney' && (lowerKey.includes('power') || lowerKey.includes('attorney') || lowerKey.includes('poa'))) {
              isMatch = true;
            }
            
            if (isMatch && masterData.documents[key]?.documentId) {
              const masterDataDoc = masterData.documents[key];
              
              // Convert backend name to frontend name
              let frontendKey;
              if (docType === 'udyogAadhaar') {
                frontendKey = 'UDYOG_AADHAAR';
              } else if (docType === 'startupCertificate') {
                frontendKey = 'STARTUP_CERTIFICATE';
              } else if (docType === 'powerOfAttorney') {
                frontendKey = 'POWER_OF_ATTORNEY';
              } else {
                frontendKey = docType.toUpperCase();
              }
              
              // Check if document is already set in form data
              const currentDoc = updatedDocuments[frontendKey];
              
              // Only set if not already set or if different from current
              if (!currentDoc?.documentId || currentDoc.documentId !== masterDataDoc.documentId) {
                updatedDocuments[frontendKey] = {
                  documentId: masterDataDoc.documentId,
                  fileName: masterDataDoc.fileName || `${docType}.pdf`,
                  documentUrl: masterDataDoc.documentUrl || masterDataDoc.fileUrl,
                  uploaded: true,
                  source: 'masterData'
                };
                hasChanges = true;
                console.log(`Auto-set ${docType} document from master data (name match) as ${frontendKey}:`, masterDataDoc.fileName);
              } else {
                console.log(`Document ${docType} already set as ${frontendKey}, skipping auto-set`);
              }
              break; // Found a match, no need to continue searching
            }
          }
        }
      }
    }

    if (hasChanges) {
      const updatedFormData = {
        ...formData,
        documents: updatedDocuments
      };
      console.log('Updated formData with documents:', updatedFormData.documents);
      setFormData(updatedFormData);
      debouncedAutoSave(updatedFormData);
      
      // Force refresh document requirements to update UI
      await renderDocumentRequirements();
      
      toast.success('Documents automatically set from master data');
    } else {
      console.log('No changes to documents');
    }
    
    console.log('=== autoSetDocumentsFromMasterData end ===');
  };

  useEffect(() => {
    if (formData.masterDataId) {
      console.log('Looking for master data with ID:', formData.masterDataId);
      console.log('Available filtered master data:', filteredMasterDataList.map(md => ({ id: md._id, name: md.clientName })));
      
      const masterData = filteredMasterDataList.find(md => md._id === formData.masterDataId);
      console.log('Found master data:', masterData ? masterData.clientName : 'Not found');
      
      setSelectedMasterData(masterData);
      
      if (masterData) {
        // Clear existing documents first when master data changes
        console.log('=== Clearing existing documents for new master data ===');
        console.log('Previous documents:', formData.documents);
        
        setFormData(prev => ({
          ...prev,
          country: masterData.country || '',
          // Clear documents to prevent showing previous master data's documents
          documents: {}
        }));
        
        // Wait a bit for state to update, then auto-set documents from new master data
        setTimeout(() => {
          console.log('=== Master data selected, attempting auto-set/generation ===');
          console.log('New master data:', masterData.clientName);
          console.log('Cleared documents:', formData.documents);
          
          // Always call autoSetDocumentsFromMasterData when master data is selected
          // The function will handle checking if documents already exist
          console.log('Calling autoSetDocumentsFromMasterData...');
          autoSetDocumentsFromMasterData(masterData).catch(error => {
            console.error('Error in autoSetDocumentsFromMasterData:', error);
          });
        }, 100);
      }
    }
  }, [formData.masterDataId, filteredMasterDataList, settings]);

  useEffect(() => {
    if (formData.country && formData.entityType) {
      calculateFees();
    }
  }, [formData.country, formData.entityType, formData.turnover]);

  const calculateFees = async () => {
    try {
      setLoadingFees(true);
      // Convert dropdown value to numeric for backend
      const turnoverValue = formData.turnover === 'less_than_2crore' ? 10000000 : 30000000; // 1 CR or 3 CR as representative values
      const response = await api.post('/trademark-applications/calculate-fees', {
        country: formData.country,
        entityType: formData.entityType,
        turnover: turnoverValue,
        udyogAadhaarAssistance: formData.udyogAadhaarAssistance // Include Udyog Aadhaar Assistance option
      });
      setFees(response.data.data);
    } catch (error) {
      console.error('Failed to calculate fees:', error);
    } finally {
      setLoadingFees(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.masterDataId) {
      newErrors.masterDataId = 'Please select a client';
    }

    if (formData.country === 'India' && !formData.turnover) {
      newErrors.turnover = 'Please select annual turnover (required for India filings)';
    }

    // Remove assignedUser validation since it's only for filtering

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  function ensureMasterDataDocuments() {
    console.log('=== ensureMasterDataDocuments called ===');
    console.log('selectedMasterData:', selectedMasterData ? selectedMasterData.clientName : 'none');
    console.log('settings:', settings ? 'available' : 'none');
    console.log('current formData.documents:', formData.documents);
    
    if (!selectedMasterData || !settings) {
      console.log('Missing selectedMasterData or settings, returning current documents');
      return formData.documents || {};
    }

    const updatedDocuments = { ...formData.documents };
    const documentTypes = ['udyogAadhaar', 'startupCertificate', 'powerOfAttorney'];

    console.log('Checking document types:', documentTypes);
    console.log('selectedMasterData.documents:', selectedMasterData.documents);
    console.log('settings.uploadFields:', settings.uploadFields);

    documentTypes.forEach(docType => {
      console.log(`\n--- Checking ${docType} ---`);
      const docConfig = settings.documentRequirements[docType];
      console.log('docConfig:', docConfig);
      
      // First, try to find document using field mapping
      let masterDataDoc = null;
      let foundFieldName = null;
      
      if (docConfig?.fieldMapping?.uploadFieldId) {
        const { uploadFieldId } = docConfig.fieldMapping;
        console.log('uploadFieldId:', uploadFieldId);
        
        const uploadField = settings.uploadFields?.find(field => field._id === uploadFieldId);
        console.log('uploadField:', uploadField);

        if (uploadField && selectedMasterData.documents?.[uploadField.name]?.documentId) {
          masterDataDoc = selectedMasterData.documents[uploadField.name];
          foundFieldName = uploadField.name;
          console.log(`Found document using field mapping for ${docType} in field: ${foundFieldName}`);
        }
      }
      
      // If not found via field mapping, try to find by common field names
      if (!masterDataDoc && selectedMasterData.documents) {
        const documentKeys = Object.keys(selectedMasterData.documents);
        console.log('Available document keys in master data:', documentKeys);
        
        // Look for documents with matching names
        for (const key of documentKeys) {
          const lowerKey = key.toLowerCase();
          let isMatch = false;
          
          if (docType === 'udyogAadhaar' && (lowerKey.includes('udyog') || lowerKey.includes('aadhaar'))) {
            isMatch = true;
          } else if (docType === 'startupCertificate' && (lowerKey.includes('startup') || lowerKey.includes('certificate'))) {
            isMatch = true;
          } else if (docType === 'powerOfAttorney' && (lowerKey.includes('power') || lowerKey.includes('attorney') || lowerKey.includes('poa'))) {
            isMatch = true;
          }
          
          if (isMatch && selectedMasterData.documents[key]?.documentId) {
            masterDataDoc = selectedMasterData.documents[key];
            foundFieldName = key;
            console.log(`Found document by name matching for ${docType} in field: ${foundFieldName}`);
            break;
          }
        }
      }
      
      // If still not found, check trademark documents
      if (!masterDataDoc && selectedMasterData.trademarkDocuments?.[docType]?.documentId) {
        masterDataDoc = selectedMasterData.trademarkDocuments[docType];
        foundFieldName = `trademarkDocuments.${docType}`;
        console.log(`Found document in trademark documents for ${docType}`);
      }

      if (masterDataDoc) {
        console.log(`Found document for ${docType} in master data:`, masterDataDoc);
        
        // Convert backend name to frontend name
        let frontendKey;
        if (docType === 'udyogAadhaar') {
          frontendKey = 'UDYOG_AADHAAR';
        } else if (docType === 'startupCertificate') {
          frontendKey = 'STARTUP_CERTIFICATE';
        } else if (docType === 'powerOfAttorney') {
          frontendKey = 'POWER_OF_ATTORNEY';
        } else {
          frontendKey = docType.toUpperCase();
        }
        
        if (!updatedDocuments[frontendKey]) {
          updatedDocuments[frontendKey] = {
            documentId: masterDataDoc.documentId,
            fileName: masterDataDoc.fileName || `${docType}.pdf`,
            documentUrl: masterDataDoc.documentUrl || masterDataDoc.fileUrl,
            uploaded: true,
            source: 'masterData',
            fieldName: foundFieldName
          };
          console.log(`Added ${docType} to updatedDocuments as ${frontendKey}:`, updatedDocuments[frontendKey]);
        } else {
          console.log(`${docType} already exists in updatedDocuments as ${frontendKey}`);
        }
      } else {
        console.log(`No document found for ${docType}`);
      }
    });

    console.log('Final updatedDocuments:', updatedDocuments);
    console.log('=== ensureMasterDataDocuments end ===');
    return updatedDocuments;
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    console.log('=== handleSubmit START ===');
    console.log('Current formData:', formData);
    console.log('selectedMasterData:', selectedMasterData ? selectedMasterData.clientName : 'none');
    console.log('Current formData.documents:', formData.documents);
    console.log('selectedMasterData.documents:', selectedMasterData?.documents);

    // Ensure master data documents are present before submit
    const mergedDocuments = ensureMasterDataDocuments();
    console.log('=== handleSubmit ===');
    console.log('mergedDocuments from ensureMasterDataDocuments:', mergedDocuments);
    console.log('Object.keys(mergedDocuments):', Object.keys(mergedDocuments || {}));
    console.log('mergedDocuments type:', typeof mergedDocuments);
    console.log('mergedDocuments is null/undefined:', mergedDocuments === null || mergedDocuments === undefined);

    console.log('Form data before validation:', formData);
    
    if (!validateForm()) {
      console.log('Validation failed:', errors);
      toast.error('Please fix the errors before proceeding');
      return;
    }

    // Convert document keys back to camelCase for backend
    const convertedDocuments = {};
    if (mergedDocuments && Object.keys(mergedDocuments).length > 0) {
      console.log('Processing mergedDocuments for backend conversion...');
      console.log('mergedDocuments keys:', Object.keys(mergedDocuments));
      
      Object.keys(mergedDocuments).forEach(docType => {
        const doc = mergedDocuments[docType];
        console.log(`Processing ${docType}:`, doc);
        console.log(`doc type:`, typeof doc);
        console.log(`doc is null/undefined:`, doc === null || doc === undefined);
        
        if (doc && (doc.uploaded || doc.documentId || doc.documentUrl)) {
          // Keep the original frontend document names - backend will handle conversion
          convertedDocuments[docType] = {
            documentId: doc.documentId,
            fileName: doc.fileName,
            documentUrl: doc.documentUrl,
            uploaded: true,
            source: doc.source
          };
          console.log(`Added ${docType} to convertedDocuments:`, convertedDocuments[docType]);
        } else {
          console.log(`Skipping ${docType} - no valid document data`);
          console.log(`doc.uploaded:`, doc?.uploaded);
          console.log(`doc.documentId:`, doc?.documentId);
          console.log(`doc.documentUrl:`, doc?.documentUrl);
        }
      });
    } else {
      console.log('No mergedDocuments or empty mergedDocuments');
      console.log('mergedDocuments:', mergedDocuments);
      console.log('mergedDocuments length:', mergedDocuments ? Object.keys(mergedDocuments).length : 'N/A');
      
      // Fallback: manually check for documents in master data
      console.log('=== FALLBACK: Manually checking master data documents ===');
      if (selectedMasterData && selectedMasterData.documents) {
        console.log('selectedMasterData.documents:', selectedMasterData.documents);
        
        // Look for documents with matching names
        Object.keys(selectedMasterData.documents).forEach(key => {
          const lowerKey = key.toLowerCase();
          let docType = null;
          
          if (lowerKey.includes('udyog') || lowerKey.includes('aadhaar')) {
            docType = 'UDYOG_AADHAAR';
          } else if (lowerKey.includes('startup') || lowerKey.includes('certificate')) {
            docType = 'STARTUP_CERTIFICATE';
          } else if (lowerKey.includes('power') || lowerKey.includes('attorney') || lowerKey.includes('poa')) {
            docType = 'POWER_OF_ATTORNEY';
          }
          
          if (docType && selectedMasterData.documents[key]?.documentId) {
            const masterDataDoc = selectedMasterData.documents[key];
            convertedDocuments[docType] = {
              documentId: masterDataDoc.documentId,
              fileName: masterDataDoc.fileName || `${docType}.pdf`,
              documentUrl: masterDataDoc.documentUrl || masterDataDoc.fileUrl,
              uploaded: true,
              source: 'masterData',
              fieldName: key
            };
            console.log(`Fallback: Added ${docType} from field ${key}:`, convertedDocuments[docType]);
          }
        });
      }
    }

    console.log('Final convertedDocuments:', convertedDocuments);
    console.log('Object.keys(convertedDocuments):', Object.keys(convertedDocuments));

    const step1Data = {
      ...formData,
      filingType: getFilingType(), // Add filing type to the submission data
      turnover: formData.turnover === 'less_than_2crore' ? 10000000 : 30000000, // Convert dropdown value to numeric
      documents: convertedDocuments, // Use merged and converted documents
      issueToClient: issueToClient, // Add invoice client option
      // Remove assignedUser from submission since it's only for filtering
    };

    console.log('Step 1 data being sent:', step1Data);
    console.log('Step 1 data documents:', step1Data.documents);
    console.log('Step 1 data documents keys:', Object.keys(step1Data.documents || {}));
    console.log('=== handleSubmit END ===');
    onComplete(step1Data);
  };

  const handleInputChange = (field, value) => {
    let updatedFormData = {
      ...formData,
      [field]: value
    };
    
    // Mark that user has interacted with the form
    if (field === 'masterDataId') {
      setUserHasInteracted(true);
      console.log('User has interacted with form - setting userHasInteracted to true');
    }
    
    // Handle user selection
    if (field === 'assignedUser') {
      setSelectedUser(value);
      
      // Update filtered master data based on selected user
      if (value) {
        console.log('User selected:', value);
        console.log('Master data list:', masterDataList);
        
        const filtered = masterDataList.filter(masterData => {
          const createdBy = masterData.createdBy;
          const userId = value._id || value;
          
          // Handle both populated and unpopulated createdBy field
          const createdById = createdBy?._id || createdBy;
          
          // If createdBy is null/undefined, include the master data (show all)
          if (!createdById) {
            console.log('Master data has no createdBy, including:', masterData.clientName);
            return true;
          }
          
          console.log('Comparing:', { createdById, userId, match: createdById === userId });
          return createdById === userId;
        });
        
        console.log('Filtered master data:', filtered);
        setFilteredMasterDataList(filtered);
      } else {
        // If no user selected, show all master data
        console.log('No user selected, showing all master data');
        setFilteredMasterDataList(masterDataList);
      }
    }
    
    // Auto-populate Country and Entity Type when client is selected
    if (field === 'masterDataId' && value) {
      // First try to find in filtered list, then in full list as fallback
      let selectedMasterData = filteredMasterDataList.find(md => md._id === value);
      if (!selectedMasterData) {
        // If not found in filtered list, search in full master data list
        selectedMasterData = masterDataList.find(md => md._id === value);
        console.log('Master data not found in filtered list, searching in full list...');
      }
              if (selectedMasterData) {
          console.log('Auto-populating from master data:', selectedMasterData);
          console.log('Master data country:', selectedMasterData.country);
          console.log('Master data country type:', typeof selectedMasterData.country);
          
          // Set the selectedMasterData state for display
          setSelectedMasterData(selectedMasterData);
          console.log('Selected master data for display:', selectedMasterData);
          console.log('Client type structure:', selectedMasterData.clientType);
          console.log('Available client types:', clientTypes);
        
        // Auto-populate country if available
        if (selectedMasterData.country) {
          // Map the country from master data to the trademark application format
          if (selectedMasterData.country === 'India') {
            updatedFormData.country = 'India';
          } else {
            updatedFormData.country = 'International';
          }
          console.log('Auto-populated country:', updatedFormData.country, 'from master data country:', selectedMasterData.country);
        } else {
          // If no country in master data, default to International
          updatedFormData.country = 'International';
          console.log('No country in master data, defaulting to International');
        }
        
        // Auto-populate entity type from client type if available
        if (selectedMasterData.clientType) {
          // Handle both ObjectId string and populated object
          const clientTypeId = selectedMasterData.clientType._id || selectedMasterData.clientType;
          updatedFormData.entityType = clientTypeId;
          console.log('Auto-populated entity type from client type:', clientTypeId);
        }
        
        // Auto-populate turnover if available and applicable
        if (selectedMasterData.turnover && selectedMasterData.country === 'India' && 
            selectedMasterData.clientType) {
          // Convert numeric turnover to dropdown value
          const turnoverValue = selectedMasterData.turnover < 20000000 ? 'less_than_2crore' : 'more_than_2crore';
          updatedFormData.turnover = turnoverValue;
          console.log('Auto-populated turnover:', turnoverValue, 'from numeric value:', selectedMasterData.turnover);
        }
        
        // Show success message
        toast.success('Client information auto-populated successfully');
      }
    } else if (field === 'masterDataId' && !value) {
      // Clear selectedMasterData when no client is selected
      setSelectedMasterData(null);
    }
    
    setFormData(updatedFormData);
    console.log('=== FORM DATA UPDATED ===');
    console.log('Updated form data:', updatedFormData);
    console.log('Country field value:', updatedFormData.country);
    console.log('Master data ID:', updatedFormData.masterDataId);
    console.log('=== END FORM DATA UPDATE ===');
    debouncedAutoSave(updatedFormData);
  };

  const handleDocumentUpload = async (file, documentType) => {
    try {
      setUploadingDocuments(prev => ({ ...prev, [documentType]: true }));
      
      // First, upload the file to get the URL using public endpoint
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await api.post('/upload/public', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (!response.data.success) {
        throw new Error('Upload failed');
      }
      
      const documentUrl = response.data.url;
      
      // Store the document URL locally in the form data
      const updatedFormData = {
        ...formData,
        documents: {
          ...formData.documents,
          [documentType]: {
            documentUrl,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
            uploaded: true,
            required: true
          }
        }
      };
      
      // Update the form data with the uploaded document
      setFormData(updatedFormData);
      
      console.log('=== handleDocumentUpload completed ===');
      console.log('Document type:', documentType);
      console.log('Updated form data:', updatedFormData);
      console.log('Document in form data:', updatedFormData.documents[documentType]);
      
      toast.success(`${documentType} uploaded successfully`);
      
      // Trigger auto-save to persist the document URL
      debouncedAutoSave(updatedFormData);
      
      // Return the updated form data
      return updatedFormData;
      
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(error.response?.data?.message || `Failed to upload ${documentType}`);
      return null;
    } finally {
      setUploadingDocuments(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg', 
      'image/png'
    ];

    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return false;
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, JPEG, PNG, or PDF files only.');
      return false;
    }

    return true;
  };

  const generatePowerOfAttorneyDocument = async () => {
    try {
      console.log('=== generatePowerOfAttorneyDocument called ===');
      console.log('settings:', settings);
      console.log('selectedMasterData:', selectedMasterData);
      
      // Check if Power of Attorney document generation is configured in settings
      const poaSettings = settings?.documentRequirements?.powerOfAttorney;
      console.log('poaSettings:', poaSettings);
      
      if (!poaSettings?.documentGenerationMapping?.enabled || !poaSettings?.documentGenerationMapping?.templateId) {
        console.log('Power of Attorney document generation not configured');
        console.log('enabled:', poaSettings?.documentGenerationMapping?.enabled);
        console.log('templateId:', poaSettings?.documentGenerationMapping?.templateId);
        return null;
      }

      // Check if we have a selected master data
      if (!selectedMasterData) {
        console.log('No master data selected for document generation');
        return null;
      }

      console.log('Generating Power of Attorney document...');
      console.log('Template ID:', poaSettings.documentGenerationMapping.templateId);
      console.log('Master Data ID:', selectedMasterData._id);
      console.log('Master Data ID type:', typeof selectedMasterData._id);
      console.log('Master Data ID value:', selectedMasterData._id);

      // Create document generation job
      const generationData = {
        templateId: poaSettings.documentGenerationMapping.templateId,
        recordId: selectedMasterData._id,
        outputFormat: 'docx',
        fileName: 'Power_of_Attorney'
      };

      console.log('Generation data:', generationData);
      console.log('Generation data JSON:', JSON.stringify(generationData, null, 2));

      const response = await api.post('/document-generation/generate', generationData);
      console.log('Generation response:', response.data);
      
      if (response.data.success) {
        const jobId = response.data.data._id;
        console.log('Document generation job created:', jobId);
        
        // Wait for the job to complete (polling)
        let attempts = 0;
        const maxAttempts = 30; // 30 seconds timeout
        
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
          
          const jobResponse = await api.get(`/document-generation/jobs/${jobId}`);
          const job = jobResponse.data.data;
          console.log(`Job status (attempt ${attempts + 1}):`, job.status);
          
          if (job.status === 'completed') {
            console.log('Power of Attorney document generated successfully');
            
            // Download the generated document
            const downloadResponse = await api.get(`/document-generation/jobs/${jobId}/download`, {
              responseType: 'blob'
            });
            
            // Create a file from the blob
            const blob = new Blob([downloadResponse.data], { 
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
            });
            const file = new File([blob], 'Power_of_Attorney.docx', { 
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
            });
            
            console.log('File created:', file.name, file.size);
            
            // Upload the generated document
            const updatedFormData = await handleDocumentUpload(file, 'POWER_OF_ATTORNEY');
            
            console.log('=== After document upload ===');
            
            if (updatedFormData && updatedFormData.documents?.POWER_OF_ATTORNEY?.uploaded) {
              console.log('✅ Power of Attorney document successfully set in form data');
              console.log('Updated form data:', updatedFormData);
              console.log('POWER_OF_ATTORNEY in documents:', updatedFormData.documents.POWER_OF_ATTORNEY);
              
              // Force a re-render by updating the document requirements
              await renderDocumentRequirements();
              
              toast.success('Power of Attorney document generated and uploaded automatically');
              return true;
            } else {
              console.log('❌ Power of Attorney document not found in form data after upload');
              console.log('Returned form data:', updatedFormData);
              toast.error('Document was generated but not set in form data');
              return false;
            }
            
          } else if (job.status === 'failed') {
            console.error('Document generation failed:', job.error);
            toast.error('Failed to generate Power of Attorney document');
            return false;
          }
          
          attempts++;
        }
        
        console.error('Document generation timed out');
        toast.error('Document generation timed out');
        return false;
        
      } else {
        console.error('Failed to create document generation job');
        return false;
      }
      
    } catch (error) {
      console.error('Error generating Power of Attorney document:', error);
      toast.error('Failed to generate Power of Attorney document');
      return false;
    }
  };

  const getFilingType = () => {
    // Use current form values instead of selectedMasterData
    const isIndia = formData.country === 'India';
    
    // If not India, return International
    if (!isIndia) {
      return 'International';
    }
    
    // Get entity type name from the current form data
    const selectedEntityType = clientTypes.find(type => type._id === formData.entityType);
    const entityTypeName = selectedEntityType?.name || '';
    
    // Get turnover from form data
    const turnover = formData.turnover;
    const isLessThan2Crore = turnover === 'less_than_2crore';
    
    console.log('Filing type calculation (using form data):', {
      entityTypeName,
      turnover,
      isLessThan2Crore,
      isIndia,
      formDataEntityType: formData.entityType,
      formDataCountry: formData.country
    });
    
    // A. Individual
    if (entityTypeName === 'Individual') {
      return 'Individual';
    }
    
    // B. Startup (client types historically "Other" or named "Startup" / "StartUp")
    if (isStartupClientTypeName(entityTypeName) && isLessThan2Crore) {
      return 'Startup';
    }
    
    // C. MSME-style entities (Partnership, LLP, Private Limited, HUF, etc.)
    if (isSmallEnterpriseStyleClientTypeName(entityTypeName)) {
      if (isLessThan2Crore) {
        return 'Small Enterprise';
      }
      return 'Others';
    }
    
    // D. All other cases
    return 'Others';
  };

  const getDocumentRequirements = async () => {
    const requirements = [];
    const isIndia = formData.country === 'India';
    
    console.log('getDocumentRequirements debug (using form data):', {
      isIndia,
      country: formData.country,
      entityType: formData.entityType,
      turnover: formData.turnover
    });
    
    // If not India, no specific document requirements
    if (!isIndia) {
      console.log('Not India, no document requirements');
      return requirements;
    }
    
    // Get entity type name from current form data
    const selectedEntityType = clientTypes.find(type => type._id === formData.entityType);
    const entityTypeName = selectedEntityType?.name || '';
    const turnover = formData.turnover;
    const isLessThan2Crore = turnover === 'less_than_2crore';
    
    console.log('Document requirements check (using form data):', {
      entityTypeName,
      turnover,
      isLessThan2Crore
    });
    
    // Helper function to check if document exists in master data
    const checkDocumentInMasterData = (documentName) => {
      // Check trademark documents first
      const mappedDocumentType = documentName === 'UDYOG_AADHAAR' ? 'udyogAadhaar' :
                                documentName === 'STARTUP_CERTIFICATE' ? 'startupCertificate' :
                                documentName === 'POWER_OF_ATTORNEY' ? 'powerOfAttorney' : null;
      
      if (mappedDocumentType && selectedMasterData?.trademarkDocuments?.[mappedDocumentType]?.documentId) {
        return true;
      }
      
      // Check general documents field using lowercase comparison
      if (selectedMasterData?.documents) {
        const documentKeys = Object.keys(selectedMasterData.documents);
        return documentKeys.some(key => {
          const lowerKey = key.toLowerCase();
          const isMatch = (documentName === 'UDYOG_AADHAAR' && (lowerKey.includes('udyog') || lowerKey.includes('aadhaar'))) ||
                         (documentName === 'STARTUP_CERTIFICATE' && (lowerKey.includes('startup') || lowerKey.includes('certificate'))) ||
                         (documentName === 'POWER_OF_ATTORNEY' && (lowerKey.includes('power') || lowerKey.includes('attorney')));
          
          return isMatch && selectedMasterData.documents[key]?.documentId;
        });
      }
      
      return false;
    };
    
    // A. Individual - No specific document requirements mentioned
    if (entityTypeName === 'Individual') {
      console.log('Individual entity type - no specific document requirements');
      return requirements;
    }
    
    // B. Startup
    if (isStartupClientTypeName(entityTypeName) && isLessThan2Crore) {
      console.log('Startup entity type with turnover less than 2CR');
      
      // Add UDYOG AADHAAR requirement if not found in master data (optional for submission later)
      if (!checkDocumentInMasterData('UDYOG_AADHAAR')) {
        requirements.push({ 
          documentName: 'UDYOG AADHAAR Certificate (Optional - can submit later)', 
          documentType: 'UDYOG_AADHAAR',
          isOptional: true 
        });
        console.log('Added UDYOG AADHAAR Certificate requirement (optional)');
      } else {
        console.log('UDYOG AADHAAR Certificate found in master data, not adding requirement');
      }
      
      // Add Startup Certificate requirement if not found in master data
      if (!checkDocumentInMasterData('STARTUP_CERTIFICATE')) {
        requirements.push({ 
          documentName: 'STARTUP Certificate', 
          documentType: 'STARTUP_CERTIFICATE',
          isOptional: false 
        });
        console.log('Added STARTUP Certificate requirement');
      } else {
        console.log('STARTUP Certificate found in master data, not adding requirement');
      }
    }
    
    // C. MSME-style entities (Partnership, LLP, Private Limited, HUF, etc.)
    if (isSmallEnterpriseStyleClientTypeName(entityTypeName) && isLessThan2Crore) {
      console.log('MSME-style entity with turnover less than 2CR');
      
      // Add UDYOG AADHAAR requirement if not found in master data (optional for submission later)
      if (!checkDocumentInMasterData('UDYOG_AADHAAR')) {
        requirements.push({ 
          documentName: 'UDYOG AADHAAR Certificate (Optional - can submit later)', 
          documentType: 'UDYOG_AADHAAR',
          isOptional: true 
        });
        console.log('Added UDYOG AADHAAR Certificate requirement (optional)');
      } else {
        console.log('UDYOG AADHAAR Certificate found in master data, not adding requirement');
      }
    }
    
    // Power of Attorney is now auto-generated and sent to user when payment is successful
    // No longer required in Step 1
    // if (!checkDocumentInMasterData('POWER_OF_ATTORNEY')) {
    //   requirements.push({ 
    //     documentName: 'POWER OF ATTORNEY', 
    //     documentType: 'POWER_OF_ATTORNEY',
    //     isOptional: false 
    //   });
    //   console.log('Added POWER OF ATTORNEY requirement');
    // } else {
    //   console.log('POWER OF ATTORNEY found in master data, not adding requirement');
    // }

    console.log('Final requirements:', requirements);
    return requirements;
  };

  const renderDocumentRequirements = async () => {
    try {
      setLoadingRequirements(true);
      const requirements = await getDocumentRequirements();
      setDocumentRequirements(requirements);
    } catch (error) {
      console.error('Error loading document requirements:', error);
      setDocumentRequirements([]);
    } finally {
      setLoadingRequirements(false);
    }
  };

  // Load document requirements when component mounts or dependencies change
  useEffect(() => {
    if (selectedMasterData && settings) {
      renderDocumentRequirements();
    }
  }, [selectedMasterData, settings, formData.country, formData.entityType, formData.turnover]);

  const renderDocumentRequirementsUI = () => {
    if (loadingRequirements) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
            <span className="text-sm text-yellow-800">Loading document requirements...</span>
          </div>
        </div>
      );
    }
    
    if (documentRequirements.length === 0) return null;

    // Get entity type and turnover info for display from current form data
    const selectedEntityType = clientTypes.find(type => type._id === formData.entityType);
    const entityTypeName = selectedEntityType?.name || '';
    const turnover = formData.turnover;
    const isLessThan2Crore = turnover === 'less_than_2crore';
    const isIndia = formData.country === 'India';

    // Use the main getFilingType function
    const filingType = getFilingType();

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <InformationCircleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-2" />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-yellow-900">Required Documents</h4>
            </div>
            
            {/* Filing Type Information */}
            <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
              <p className="text-xs text-blue-800">
                <strong>Filing Type:</strong> {filingType} | 
                <strong> Entity:</strong> {entityTypeName} | 
                <strong> Turnover:</strong> {isLessThan2Crore ? 'Less than 2 Crore' : 'More than 2 Crore'}
              </p>
            </div>
            
            <ul className="text-sm text-yellow-800 space-y-1">
              {documentRequirements.map((req, index) => {
                const isOptional = req.isOptional;
                const cleanReq = req.documentName.replace(/\s+/g, '_').toUpperCase();
                
                return (
                  <li key={index} className="flex items-center">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                    {req.documentName} {isOptional && <span className="text-blue-600 text-xs">(Optional)</span>}
                  </li>
                );
              })}
            </ul>
            
            {/* Additional context for specific requirements */}
            {documentRequirements.some(req => req.isOptional) && (
              <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
                <p className="text-xs text-orange-800">
                  <strong>Note:</strong> UDYOG AADHAAR Certificate is optional and can be submitted later. 
                  You can proceed to Step 2 without uploading this document.
                </p>
              </div>
            )}
            
            {documentRequirements.some(req => req.documentName.includes('STARTUP Certificate')) && (
              <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                <p className="text-xs text-purple-800">
                  <strong>Note:</strong> STARTUP Certificate is required when the filing type is Startup (client types named Startup, StartUp, or legacy Other) and turnover is under 2 Crore.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentUploads = () => {
    console.log('renderDocumentUploads called with requirements:', documentRequirements);
    
    if (documentRequirements.length === 0) {
      console.log('No requirements, returning null');
      return null;
    }

    console.log('Rendering document uploads for:', documentRequirements);
    
    // Calculate document status
    const documentStatus = documentRequirements.map(req => {
      const { documentName, documentType, isOptional } = req;
      
      const localDoc = formData.documents?.[documentType];
      const appDoc = application?.documents?.[documentType];
      
      // Check for documents in master data using field mappings from settings
      const masterDataDoc = (() => {
        if (!settings || !selectedMasterData) return null;
        
        // Get the document configuration from settings
        const mappedDocumentType = documentType === 'UDYOG_AADHAAR' ? 'udyogAadhaar' :
                                  documentType === 'STARTUP_CERTIFICATE' ? 'startupCertificate' :
                                  documentType === 'POWER_OF_ATTORNEY' ? 'powerOfAttorney' : documentType;
        
        const docConfig = settings.documentRequirements[mappedDocumentType];
        if (!docConfig?.fieldMapping) return null;
        
        const { uploadFieldId } = docConfig.fieldMapping;
        
        // If upload field is configured, check for documents in that field
        if (uploadFieldId && selectedMasterData.documents) {
          // Find the upload field by ID
          const uploadField = settings.uploadFields?.find(field => field._id === uploadFieldId);
          if (uploadField && selectedMasterData.documents[uploadField.name]?.documentId) {
            return {
              documentId: selectedMasterData.documents[uploadField.name].documentId,
              fileName: selectedMasterData.documents[uploadField.name].fileName || `${documentType}.pdf`,
              documentUrl: selectedMasterData.documents[uploadField.name].documentUrl,
              uploaded: true,
              source: 'masterData',
              fieldName: uploadField.name
            };
          }
        }
        
        return null;
      })();
      
      // Priority: Local form data > Application data > Master data
      const isUploaded = localDoc?.uploaded || appDoc?.uploaded || masterDataDoc?.uploaded;
      
      console.log(`Document status for ${documentName}:`, {
        documentType,
        localDoc: localDoc ? 'found' : 'not found',
        appDoc: appDoc ? 'found' : 'not found',
        masterDataDoc: masterDataDoc ? 'found' : 'not found',
        isUploaded,
        isOptional
      });
      
      return {
        name: documentName,
        documentType,
        isOptional,
        isUploaded,
        status: isUploaded ? 'uploaded' : isOptional ? 'optional' : 'required'
      };
    });
    
    const requiredDocs = documentStatus.filter(doc => !doc.isOptional);
    const optionalDocs = documentStatus.filter(doc => doc.isOptional);
    const uploadedRequired = requiredDocs.filter(doc => doc.isUploaded).length;
    const uploadedOptional = optionalDocs.filter(doc => doc.isUploaded).length;
    
    console.log('Document count summary:', {
      totalRequirements: documentStatus.length,
      requiredDocs: requiredDocs.length,
      optionalDocs: optionalDocs.length,
      uploadedRequired,
      uploadedOptional,
      totalUploaded: uploadedRequired + uploadedOptional,
      formDataDocuments: Object.keys(formData.documents || {}),
      applicationDocuments: Object.keys(application?.documents || {}),
      selectedMasterData: selectedMasterData ? selectedMasterData.clientName : 'none'
    });
    
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Upload Required Documents</h3>
        
        {/* Document Status Summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{uploadedRequired}/{requiredDocs.length}</div>
              <div className="text-sm text-gray-600">Required Documents</div>
              {uploadedRequired < requiredDocs.length && (
                <div className="text-xs text-red-600 mt-1">
                  {requiredDocs.length - uploadedRequired} missing
                </div>
              )}
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{uploadedOptional}/{optionalDocs.length}</div>
              <div className="text-sm text-gray-600">Optional Documents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{uploadedRequired + uploadedOptional}/{documentStatus.length}</div>
              <div className="text-sm text-gray-600">Total Uploaded</div>
            </div>
          </div>
          
          {/* Progress indicator */}
          {requiredDocs.length > 0 && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Required Documents Progress</span>
                <span>{uploadedRequired}/{requiredDocs.length}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadedRequired / requiredDocs.length) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
        
        {documentRequirements.map((req, index) => {
          const isOptional = req.isOptional;
          const { documentName, documentType } = req;
          
          const isUploading = uploadingDocuments[documentType];
          const mappedDocumentType = documentType === 'UDYOG_AADHAAR' ? 'udyogAadhaar' :
                                    documentType === 'STARTUP_CERTIFICATE' ? 'startupCertificate' :
                                    documentType === 'POWER_OF_ATTORNEY' ? 'powerOfAttorney' : documentType;
          
          // Check for uploaded documents in multiple sources
          const localDoc = formData.documents?.[documentType];
          const appDoc = application?.documents?.[mappedDocumentType];
          
          // Debug logging for document detection
          console.log(`Document detection for ${documentName}:`, {
            documentType,
            mappedDocumentType,
            localDoc: formData.documents?.[documentType],
            appDoc: application?.documents?.[mappedDocumentType],
            allFormDataDocuments: Object.keys(formData.documents || {}),
            allApplicationDocuments: Object.keys(application?.documents || {})
          });
          
          // Check for documents in master data using field mappings from settings
          const masterDataDoc = (() => {
            if (!settings || !selectedMasterData) return null;
            
            // Get the document configuration from settings
            const docConfig = settings.documentRequirements[mappedDocumentType];
            if (!docConfig?.fieldMapping) return null;
            
            const { uploadFieldId, masterDataField } = docConfig.fieldMapping;
            
            // If upload field is configured, check for documents in that field
            if (uploadFieldId && selectedMasterData.documents) {
              // Find the upload field by ID
              const uploadField = settings.uploadFields?.find(field => field._id === uploadFieldId);
              if (uploadField && selectedMasterData.documents[uploadField.name]?.fileUrl) {
                return {
                  documentId: selectedMasterData.documents[uploadField.name].documentId,
                  fileName: selectedMasterData.documents[uploadField.name].fileName || `${documentType}.pdf`,
                  documentUrl: selectedMasterData.documents[uploadField.name].documentUrl,
                  uploaded: true,
                  source: 'masterData',
                  fieldName: uploadField.name
                };
              }
            }
            
            // If master data field is configured, check for documents in that field
            if (masterDataField && selectedMasterData.documents) {
              if (selectedMasterData.documents[masterDataField]?.documentId) {
                return {
                  documentId: selectedMasterData.documents[masterDataField].documentId,
                  fileName: selectedMasterData.documents[masterDataField].fileName || `${documentType}.pdf`,
                  documentUrl: selectedMasterData.documents[masterDataField].documentUrl,
                  uploaded: true,
                  source: 'masterData',
                  fieldName: masterDataField
                };
              }
            }
            
            // Fallback: Check trademark documents first
            if (selectedMasterData?.trademarkDocuments?.[mappedDocumentType]?.documentId) {
              return {
                documentId: selectedMasterData.trademarkDocuments[mappedDocumentType].documentId,
                fileName: selectedMasterData.trademarkDocuments[mappedDocumentType].fileName || `${documentType}.pdf`,
                documentUrl: selectedMasterData.trademarkDocuments[mappedDocumentType].documentUrl,
                uploaded: true,
                source: 'masterData'
              };
            }
            
            // Fallback: Check general documents field using lowercase comparison
            if (selectedMasterData?.documents) {
              const documentKeys = Object.keys(selectedMasterData.documents);
              for (const key of documentKeys) {
                const lowerKey = key.toLowerCase();
                const isMatch = (documentName === 'UDYOG AADHAAR Certificate' && (lowerKey.includes('udyog') || lowerKey.includes('aadhaar'))) ||
                               (documentName === 'STARTUP Certificate' && (lowerKey.includes('startup') || lowerKey.includes('certificate'))) ||
                               (documentName === 'POWER OF ATTORNEY' && (lowerKey.includes('power') || lowerKey.includes('attorney')));
                
                if (isMatch && selectedMasterData.documents[key]?.documentId) {
                  return {
                    documentId: selectedMasterData.documents[key].documentId,
                    fileName: selectedMasterData.documents[key].fileName || `${documentType}.pdf`,
                    documentUrl: selectedMasterData.documents[key].documentUrl,
                    uploaded: true,
                    source: 'masterData'
                  };
                }
              }
            }
            
            return null;
          })();
          
          // Determine if document is uploaded and get the best source
          // Priority: Local form data > Application data > Master data
          const isUploaded = localDoc?.uploaded || appDoc?.uploaded || masterDataDoc?.uploaded;
          const uploadedDoc = localDoc || appDoc || masterDataDoc;
          const isFromMasterData = masterDataDoc?.uploaded && !localDoc?.uploaded && !appDoc?.uploaded;
          const isAutoSet = localDoc?.source === 'masterData';
          
          console.log(`Document ${documentName}:`, { 
            documentType, 
            isUploaded, 
            uploadedDoc, 
            isOptional,
            isFromMasterData,
            isAutoSet,
            masterDataDoc: masterDataDoc ? 'found' : 'not found',
            fieldMapping: settings?.documentRequirements?.[mappedDocumentType]?.fieldMapping
          });
          
          return (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-sm font-medium text-gray-900">
                  {req.documentName} {isOptional ? '(Optional)' : '*'}
                </h5>
                {isUploaded && (
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    <span className="text-xs">Uploaded</span>
                  </div>
                )}
              </div>

              {isOptional && !isUploaded && (
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> This document is optional and can be submitted later. 
                    You can proceed to Step 2 without uploading this document.
                  </p>
                </div>
              )}

              {isUploaded ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-green-500 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-green-900">
                          {uploadedDoc?.fileName || 'Document uploaded successfully'}
                        </p>
                        <p className="text-xs text-green-700">
                          {isAutoSet ? 'Auto-set from master data' :
                           isFromMasterData ? 'Available from master data' : 
                           appDoc?.uploaded && !localDoc?.uploaded ? 'Auto-populated from application' : 
                           'Uploaded successfully'}
                        </p>
                        {uploadedDoc?.documentUrl && (
                          <div className="flex items-center space-x-2 mt-1">
                            <a
                              href={uploadedDoc.documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              View Document
                            </a>
                            <a
                              href={uploadedDoc.documentUrl}
                              download={uploadedDoc.fileName}
                              className="text-xs text-green-600 hover:text-green-800"
                            >
                              Download
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {isAutoSet && (
                        <button
                          type="button"
                          onClick={() => {
                            // Remove auto-set document
                            const updatedFormData = {
                              ...formData,
                              documents: {
                                ...formData.documents,
                                [documentType]: null
                              }
                            };
                            setFormData(updatedFormData);
                            debouncedAutoSave(updatedFormData);
                            toast.success('Document removed from application');
                          }}
                          className="text-orange-500 hover:text-orange-700 text-xs"
                        >
                          Remove
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          // Remove document from local formData
                          const updatedFormData = {
                            ...formData,
                            documents: {
                              ...formData.documents,
                              [documentType]: null
                            }
                          };
                          setFormData(updatedFormData);
                          debouncedAutoSave(updatedFormData);
                        }}
                        className="text-red-500 hover:text-red-700"
                        title="Replace document"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center">
                  {/* Check if document generation is available for Power of Attorney */}
                  {documentType === 'POWER_OF_ATTORNEY' && 
                   settings?.documentRequirements?.powerOfAttorney?.documentGenerationMapping?.enabled &&
                   settings?.documentRequirements?.powerOfAttorney?.documentGenerationMapping?.templateId && (
                    <div className="mb-3">
                      <button
                        type="button"
                        onClick={async () => {
                          setGeneratingDocuments(prev => ({ ...prev, [documentType]: true }));
                          try {
                            const result = await generatePowerOfAttorneyDocument();
                            if (result) {
                              // Document was generated and uploaded successfully
                              toast.success('Power of Attorney document generated and uploaded automatically');
                            }
                          } catch (error) {
                            console.error('Error generating document:', error);
                            toast.error('Failed to generate Power of Attorney document');
                          } finally {
                            setGeneratingDocuments(prev => ({ ...prev, [documentType]: false }));
                          }
                        }}
                        disabled={generatingDocuments[documentType]}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {generatingDocuments[documentType] ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <DocumentIcon className="h-3 w-3 mr-2" />
                            Generate Document
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-500 mt-1">
                        Automatically generate Power of Attorney using master data
                      </p>
                    </div>
                  )}
                  
                  <CloudArrowUpIcon className="mx-auto h-6 w-6 text-gray-400" />
                  <div className="mt-1">
                    <label
                      htmlFor={`file-upload-${documentType}`}
                      className="cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Upload a file</span>
                      <input
                        id={`file-upload-${documentType}`}
                        type="file"
                        className="sr-only"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            const file = e.target.files[0];
                            if (validateFile(file)) {
                              handleDocumentUpload(file, documentType);
                            }
                          }
                        }}
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/jpg,image/png"
                        disabled={isUploading}
                      />
                    </label>
                    <p className="text-xs text-gray-500">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, JPG, PNG up to 5MB
                  </p>
                  {isUploading && (
                    <div className="mt-1">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                        <span className="text-xs text-blue-600">Uploading...</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Global test function for debugging (can be called from browser console)
  useEffect(() => {
    // Make the function available globally for testing
    window.testPowerOfAttorneyGeneration = async () => {
      console.log('=== Manual Power of Attorney Generation Test ===');
      console.log('selectedMasterData:', selectedMasterData);
      console.log('settings:', settings);
      
      if (!selectedMasterData) {
        console.error('No master data selected');
        return;
      }
      
      if (!settings) {
        console.error('No settings available');
        return;
      }
      
      const poaSettings = settings?.documentRequirements?.powerOfAttorney;
      console.log('Power of Attorney settings:', poaSettings);
      
      if (!poaSettings?.documentGenerationMapping?.enabled) {
        console.error('Power of Attorney document generation not enabled');
        return;
      }
      
      if (!poaSettings?.documentGenerationMapping?.templateId) {
        console.error('No template ID configured for Power of Attorney');
        return;
      }
      
      console.log('Attempting manual generation...');
      try {
        const result = await generatePowerOfAttorneyDocument();
        console.log('Generation result:', result);
      } catch (error) {
        console.error('Generation error:', error);
      }
    };

    // Add debug function for autoSetDocumentsFromMasterData
    window.testAutoSetDocuments = async () => {
      console.log('=== Manual Auto Set Documents Test ===');
      console.log('selectedMasterData:', selectedMasterData);
      console.log('settings:', settings);
      
      if (!selectedMasterData) {
        console.error('No master data selected');
        return;
      }
      
      if (!settings) {
        console.error('No settings available');
        return;
      }
      
      console.log('Attempting auto set documents...');
      try {
        await autoSetDocumentsFromMasterData(selectedMasterData);
        console.log('Auto set documents completed');
      } catch (error) {
        console.error('Auto set documents error:', error);
      }
    };
    
    // Cleanup function
    return () => {
      delete window.testPowerOfAttorneyGeneration;
      delete window.testAutoSetDocuments;
    };
  }, [selectedMasterData, settings]);

  // Add debug function for Power of Attorney settings
  window.checkPowerOfAttorneySettings = () => {
    console.log('=== Power of Attorney Settings Check ===');
    console.log('settings:', settings);
    console.log('settings?.documentRequirements:', settings?.documentRequirements);
    console.log('powerOfAttorney config:', settings?.documentRequirements?.powerOfAttorney);
    console.log('documentGenerationMapping:', settings?.documentRequirements?.powerOfAttorney?.documentGenerationMapping);
    
    const poaConfig = settings?.documentRequirements?.powerOfAttorney;
    if (poaConfig) {
      console.log('Power of Attorney Configuration:', {
        hasFieldMapping: !!poaConfig.fieldMapping,
        hasDocumentGenerationMapping: !!poaConfig.documentGenerationMapping,
        generationEnabled: poaConfig.documentGenerationMapping?.enabled,
        hasTemplateId: !!poaConfig.documentGenerationMapping?.templateId,
        templateId: poaConfig.documentGenerationMapping?.templateId
      });
    } else {
      console.log('No Power of Attorney configuration found');
    }
  };

  // Add debug function for manual document generation (only way to generate now)
  window.testManualPowerOfAttorneyGeneration = async () => {
    console.log('=== Manual Power of Attorney Generation Test ===');
    console.log('selectedMasterData:', selectedMasterData);
    console.log('settings:', settings);
    
    if (!selectedMasterData) {
      console.error('No master data selected');
      return;
    }
    
    if (!settings) {
      console.error('No settings available');
      return;
    }
    
    const poaSettings = settings?.documentRequirements?.powerOfAttorney;
    console.log('Power of Attorney settings:', poaSettings);
    
    if (!poaSettings?.documentGenerationMapping?.enabled) {
      console.error('Power of Attorney document generation not enabled');
      return;
    }
    
    if (!poaSettings?.documentGenerationMapping?.templateId) {
      console.error('No template ID configured for Power of Attorney');
      return;
    }
    
    console.log('Attempting manual generation...');
    try {
      const result = await generatePowerOfAttorneyDocument();
      console.log('Generation result:', result);
    } catch (error) {
      console.error('Generation error:', error);
    }
  };

  // Add debug function for clearing documents
  window.clearAndResetDocuments = async () => {
    console.log('=== Manual Clear and Reset Documents ===');
    console.log('Current formData:', formData);
    console.log('Current documents:', formData.documents);
    
    if (!selectedMasterData) {
      console.error('No master data selected');
      return;
    }
    
    // Clear documents
    setFormData(prev => ({
      ...prev,
      documents: {}
    }));
    
    console.log('Documents cleared');
    
    // Wait a bit, then auto-set from master data
    setTimeout(async () => {
      console.log('Auto-setting documents from master data...');
      await autoSetDocumentsFromMasterData(selectedMasterData);
      console.log('Documents auto-set completed');
    }, 200);
  };

  // Add debug function for document status
  window.checkDocumentStatus = () => {
    console.log('=== Document Status Check ===');
    console.log('selectedMasterData:', selectedMasterData);
    console.log('formData.documents:', formData.documents);
    console.log('application.documents:', application?.documents);
    
    // Check Power of Attorney specifically
    const poaInFormData = formData.documents?.POWER_OF_ATTORNEY;
    const poaInMasterData = selectedMasterData?.documents?.POWER_OF_ATTORNEY;
    const poaInApplication = application?.documents?.POWER_OF_ATTORNEY;
    
    console.log('Power of Attorney Status:');
    console.log('- In formData:', poaInFormData);
    console.log('- In masterData:', poaInMasterData);
    console.log('- In application:', poaInApplication);
    
    // Check if document is considered uploaded
    const isUploaded = poaInFormData?.uploaded || poaInApplication?.uploaded || poaInMasterData?.documentId;
    console.log('- Is uploaded:', isUploaded);
    
    // Check document generation settings
    const poaSettings = settings?.documentRequirements?.powerOfAttorney;
    console.log('Power of Attorney Settings:', poaSettings);
    console.log('- Document generation enabled:', poaSettings?.documentGenerationMapping?.enabled);
    console.log('- Template ID:', poaSettings?.documentGenerationMapping?.templateId);
    
    return {
      isUploaded,
      poaInFormData,
      poaInMasterData,
      poaInApplication,
      poaSettings
    };
  };

  // Add function to manually set Power of Attorney document for testing
  window.setPowerOfAttorneyDocument = (documentUrl, fileName) => {
    console.log('=== Manually Setting Power of Attorney Document ===');
    
    const testDocument = {
      documentId: 'test-poa-document-' + Date.now(),
      fileName: fileName || 'Power_of_Attorney_Test.docx',
      documentUrl: documentUrl || 'http://localhost:5000/uploads/test-poa.docx',
      uploaded: true,
      source: 'manual'
    };
    
    console.log('Setting test document:', testDocument);
    
    const updatedFormData = {
      ...formData,
      documents: {
        ...formData.documents,
        POWER_OF_ATTORNEY: testDocument
      }
    };
    
    setFormData(updatedFormData);
    debouncedAutoSave(updatedFormData);
    
    console.log('Updated formData:', updatedFormData);
    toast.success('Power of Attorney document set manually for testing');
    
    return testDocument;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Step 1: Basic Information</h2>
          
          {/* Save Status and Manual Save Button */}
          <div className="flex items-center space-x-3">
            {autoSaving && (
              <div className="flex items-center text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm">Auto-saving...</span>
              </div>
            )}
            
            {!application?._id && !autoSaving && (
              <div className="flex items-center text-gray-500">
                <InformationCircleIcon className="h-4 w-4 mr-1" />
                <span className="text-sm">New application - will be saved when completed</span>
              </div>
            )}
            
            {lastSaved && !autoSaving && application?._id && (
              <div className="flex items-center text-green-600">
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                <span className="text-sm">
                  Saved {lastSaved.toLocaleTimeString()}
                </span>
              </div>
            )}
            
            <button
              type="button"
              onClick={manualSave}
              disabled={autoSaving || saving}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {autoSaving ? 'Saving...' : application?._id ? 'Save Progress' : 'Save Later'}
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* User Assignment - Only show for new applications */}
          {!application?._id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by User
                {currentUser?.role !== 'admin' && (
                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Current User
                  </span>
                )}
              </label>
              {currentUser?.role === 'admin' ? (
                <UserSelector
                  selectedUser={selectedUser}
                  onUserChange={(user) => {
                    handleInputChange('assignedUser', user);
                    setSelectedUser(user);
                  }}
                  userList={users}
                  placeholder="Select a user to filter master data (optional)..."
                />
              ) : (
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                  {currentUser?.firstName} {currentUser?.lastName} ({currentUser?.email})
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Select a user to filter master data. Leave empty to see all clients.
              </p>
            </div>
          )}

          {/* Master Data Selection - Only show for new applications */}
          {!application?._id ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Client *
                <span className="ml-2 text-xs text-gray-500">
                  ({filteredMasterDataList.length} clients available)
                </span>
              </label>
              <select
                value={formData.masterDataId}
                onChange={(e) => handleInputChange('masterDataId', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.masterDataId ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select a client</option>
                {(() => {
                  console.log('Rendering dropdown with filteredMasterDataList:', filteredMasterDataList);
                  return filteredMasterDataList.map((masterData) => (
                    <option key={masterData._id} value={masterData._id}>
                      {masterData.clientName} - {masterData.country || 'No country'}
                    </option>
                  ));
                })()}
              </select>
              {errors.masterDataId && (
                <p className="mt-1 text-sm text-red-600">{errors.masterDataId}</p>
              )}
              
              {/* Issue to Client Checkbox */}
              {formData.masterDataId && (
                <div className="mt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={issueToClient}
                      onChange={(e) => setIssueToClient(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Issue invoice to this client
                    </span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    When checked, the invoice will be issued to the selected client. When unchecked, it will be issued to the current user.
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Show selected client information for existing applications */
            selectedMasterData && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-blue-900">Selected Client</h3>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Locked
                  </span>
                </div>
                <div className="text-sm text-blue-800">
                  <div className="font-medium">{selectedMasterData.clientName}</div>
                  <div className="text-blue-600">{selectedMasterData.country || 'No country'}</div>
                </div>
              </div>
            ) 
          )}

          {/* Client Information Display */}
          {selectedMasterData && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Client Information</h3>
              {console.log('Rendering client information with selectedMasterData:', selectedMasterData)}
              {console.log('Available clientTypes:', clientTypes)}
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {/* Basic Information */}
                <div>
                  <dt className="text-gray-500 font-medium">Client Name</dt>
                  <dd className="text-gray-900">{selectedMasterData.clientName}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Client Type</dt>
                  <dd className="text-gray-900">
                    {(() => {
                      const clientType = selectedMasterData.clientType;
                      if (clientType) {
                        // Handle populated object
                        if (typeof clientType === 'object' && clientType.name) {
                          return clientType.name;
                        }
                        // Handle string or ObjectId
                        if (typeof clientType === 'string') {
                          // Try to find the client type name from the clientTypes array
                          const foundClientType = clientTypes.find(ct => ct._id === clientType);
                          return foundClientType ? foundClientType.name : clientType;
                        }
                        return clientType;
                      }
                      return 'Not provided';
                    })()}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Country</dt>
                  <dd className="text-gray-900">{selectedMasterData.country || 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">State</dt>
                  <dd className="text-gray-900">{selectedMasterData.state || 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Region</dt>
                  <dd className="text-gray-900">{selectedMasterData.region || 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Pincode</dt>
                  <dd className="text-gray-900">{selectedMasterData.pincode || 'Not provided'}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-gray-500 font-medium">Address</dt>
                  <dd className="text-gray-900">{selectedMasterData.clientAddress || 'Not provided'}</dd>
                </div>
                
                {/* Phone Number */}
                {selectedMasterData.phoneNumber && (
                  <div>
                    <dt className="text-gray-500 font-medium">Phone Number</dt>
                    <dd className="text-gray-900">
                      {selectedMasterData.phoneNumber.countryCode && `${selectedMasterData.phoneNumber.countryCode} `}
                      {selectedMasterData.phoneNumber.number || 'Not provided'}
                    </dd>
                  </div>
                )}
                
                {/* Proprietor Firm Name */}
                {selectedMasterData.proprietorFirmName && (
                  <div>
                    <dt className="text-gray-500 font-medium">Proprietor Firm Name</dt>
                    <dd className="text-gray-900">{selectedMasterData.proprietorFirmName}</dd>
                  </div>
                )}
                
                {/* Turnover */}
                {selectedMasterData.turnover && (
                  <div>
                    <dt className="text-gray-500 font-medium">Annual Turnover</dt>
                    <dd className="text-gray-900">
                      {selectedMasterData.turnover < 20000000 ? 'Less than 2 Crore' : 'More than 2 Crore'}
                    </dd>
                  </div>
                )}
                
                {/* Partners */}
                {selectedMasterData.partners && selectedMasterData.partners.length > 0 && (
                  <div className="md:col-span-2">
                    <dt className="text-gray-500 font-medium">Partners</dt>
                    <dd className="text-gray-900">
                      <ul className="list-disc list-inside space-y-1">
                        {selectedMasterData.partners.map((partner, index) => (
                          <li key={index}>{partner.name}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
                
                {/* Contact Persons */}
                {selectedMasterData.contactPersons && selectedMasterData.contactPersons.length > 0 && (
                  <div className="md:col-span-2">
                    <dt className="text-gray-500 font-medium">Contact Persons</dt>
                    <dd className="text-gray-900">
                      <div className="space-y-3">
                        {selectedMasterData.contactPersons.map((contact, index) => (
                          <div key={index} className="border-l-2 border-gray-200 pl-3">
                            <div className="font-medium">{contact.title} {contact.name}</div>
                            <div className="text-gray-600">{contact.email}</div>
                            {contact.phoneNumbers && contact.phoneNumbers.length > 0 && (
                              <div className="text-gray-600">
                                {contact.phoneNumbers.join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </dd>
                  </div>
                )}
                
                {/* Authorized Signatories */}
                {selectedMasterData.authorizedSignatories && selectedMasterData.authorizedSignatories.length > 0 && (
                  <div className="md:col-span-2">
                    <dt className="text-gray-500 font-medium">Authorized Signatories</dt>
                    <dd className="text-gray-900">
                      <div className="space-y-3">
                        {selectedMasterData.authorizedSignatories.map((signatory, index) => (
                          <div key={index} className="border-l-2 border-gray-200 pl-3">
                            <div className="font-medium">{signatory.title} {signatory.designationName}</div>
                            {signatory.fatherName && (
                              <div className="text-gray-600">Father: {signatory.fatherName}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </dd>
                  </div>
                )}
                
                {/* Trademark Documents Status */}
                {selectedMasterData.trademarkDocuments && (
                  <div className="md:col-span-2">
                    <dt className="text-gray-500 font-medium">Trademark Documents</dt>
                    <dd className="text-gray-900">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${selectedMasterData.trademarkDocuments.udyogAadhaar?.documentId ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span className="text-sm">Udyog Aadhaar</span>
                        </div>
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${selectedMasterData.trademarkDocuments.startupCertificate?.documentId ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span className="text-sm">Startup Certificate</span>
                        </div>
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${selectedMasterData.trademarkDocuments.powerOfAttorney?.documentId ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <span className="text-sm">Power of Attorney</span>
                        </div>
                      </div>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Country Selection - Hidden since auto-filled when selecting client */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country *
            </label>
            <select
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.country ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select a country</option>
              <option value="India">India</option>
              <option value="International">International</option>
            </select>
            {errors.country && (
              <p className="mt-1 text-sm text-red-600">{errors.country}</p>
            )}
          </div> */}

          {/* Entity Type Selection - Hidden since auto-filled when selecting client */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entity Type *
            </label>
            <select
              value={formData.entityType}
              onChange={(e) => handleInputChange('entityType', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.entityType ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select an entity type</option>
              {clientTypes.map((clientType) => (
                <option key={clientType._id} value={clientType._id}>
                  {clientType.name}
                </option>
              ))}
            </select>
            {errors.entityType && (
              <p className="mt-1 text-sm text-red-600">{errors.entityType}</p>
            )}
          </div> */}

          {/* Turnover: required for India filing-type + document upload rules when not set on client */}
          {formData.country === 'India' && selectedMasterData && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annual turnover (India) *
              </label>
              <select
                value={formData.turnover}
                onChange={(e) => handleInputChange('turnover', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.turnover ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select turnover band</option>
                <option value="less_than_2crore">Less than 2 Crore</option>
                <option value="more_than_2crore">2 Crore or more</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Used for filing category, fees, and which supporting documents you can upload. Pre-filled from the client when available.
              </p>
              {errors.turnover && (
                <p className="mt-1 text-sm text-red-600">{errors.turnover}</p>
              )}
            </div>
          )}

          {/* Udyog Aadhaar Assistance - Only for India */}
          {formData.country === 'India' && (
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.udyogAadhaarAssistance}
                  onChange={(e) => handleInputChange('udyogAadhaarAssistance', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Apply for Udyog Aadhaar Assistance
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Check this if you want to apply for Udyog Aadhaar assistance along with your trademark application.
              </p>
            </div>
          )}

          {/* Fee Calculation */}
          {fees && showFees && !feeSettingsLoading && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-3">Fee Calculation</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500 font-medium">Government Fees</dt>
                  <dd className="text-gray-900">₹{fees.governmentFees?.toLocaleString() || '0'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Professional Fees</dt>
                  <dd className="text-gray-900">₹{fees.professionalFees?.toLocaleString() || '0'}</dd>
                </div>
                {formData.udyogAadhaarAssistance && fees.udyogAadhaarFees > 0 && (
                  <div>
                    <dt className="text-gray-500 font-medium">Udyog Aadhaar Fees</dt>
                    <dd className="text-gray-900">₹{fees.udyogAadhaarFees?.toLocaleString() || '0'}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-gray-500 font-medium">Total Fees</dt>
                  <dd className="text-gray-900 font-semibold">₹{fees.totalFees?.toLocaleString() || '0'}</dd>
                </div>
              </dl>
            </div>
          )}



          {/* Document Requirements */}
          {renderDocumentRequirementsUI()}

          {/* Document Uploads */}
          {renderDocumentUploads()}

          {/* Submit Button */}
          <div className="flex justify-between pt-6">
            <button
              type="button"
              onClick={onPreviousStep}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Previous
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Next Step'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrademarkStep1;